class Slideshow {
  constructor(element) {
    this.element = element;
    this.container = element.querySelector('.slideshow-container');
    this.slides = Array.from(element.querySelectorAll('.slideshow-slide'));
    this.dots = Array.from(element.querySelectorAll('.slideshow-dot'));
    this.prevBtn = element.querySelector('.slideshow-arrow--prev');
    this.nextBtn = element.querySelector('.slideshow-arrow--next');
    this.pauseBtn = element.querySelector('.slideshow-pause');
    this.counterCurrent = element.querySelector('.slideshow-counter-current');
    
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isAnimating = false;
    this.autoplayEnabled = element.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(element.dataset.speed) || 5000;
    this.transitionEffect = element.dataset.transition || 'slide';
    this.loopEnabled = element.dataset.loop === 'true';
    this.pauseOnHover = element.dataset.pauseHover === 'true';
    this.mobileSwipeEnabled = element.dataset.mobileSwipe === 'true';
    
    this.autoplayInterval = null;
    this.progressInterval = null;
    this.startTime = null;
    this.remainingTime = this.autoplaySpeed;
    
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    
    this.init();
  }
  
  init() {
    if (this.totalSlides === 0) return;
    
    this.setupAccessibility();
    this.setupEventListeners();
    this.setupResizeObserver();
    this.setupIntersectionObserver();
    
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
    
    this.updateCounter();
    this.setInitialSlideHeight();
    
    // Initialize AOS if available
    if (typeof AOS !== 'undefined') {
      AOS.refresh();
    }
  }
  
  setupAccessibility() {
    this.element.setAttribute('aria-live', 'polite');
    this.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `${index + 1} of ${this.totalSlides}`);
      slide.setAttribute('aria-roledescription', 'slide');
    });
  }
  
  setupEventListeners() {
    // Arrow navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }
    
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Pause button
    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', () => this.togglePause());
    }
    
    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => {
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.totalSlides - 1);
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          this.togglePause();
          break;
      }
    });
    
    // Pause on hover
    if (this.pauseOnHover && this.autoplayEnabled) {
      this.element.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.element.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.element.addEventListener('focusin', () => this.pauseAutoplay());
      this.element.addEventListener('focusout', () => this.resumeAutoplay());
    }
    
    // Mobile swipe
    if (this.mobileSwipeEnabled && 'ontouchstart' in window) {
      this.setupTouchEvents();
    }
    
    // Visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (this.autoplayEnabled && !this.isPaused()) {
        this.resumeAutoplay();
      }
    });
    
    // Window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }
  
  setupTouchEvents() {
    this.container.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
      this.pauseAutoplay();
    }, { passive: true });
    
    this.container.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe();
      if (!this.isPaused()) {
        this.resumeAutoplay();
      }
    }, { passive: true });
  }
  
  handleSwipe() {
    const threshold = 50;
    const swipeDistance = this.touchEndX - this.touchStartX;
    const verticalDistance = Math.abs(this.touchEndY - this.touchStartY);
    
    // Only horizontal swipes
    if (Math.abs(swipeDistance) < threshold || verticalDistance > 100) return;
    
    if (swipeDistance > 0) {
      this.prevSlide();
    } else {
      this.nextSlide();
    }
  }
  
  setupResizeObserver() {
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          if (entry.target === this.container) {
            this.updateSlideHeights();
          }
        }
      });
      
      resizeObserver.observe(this.container);
    }
  }
  
  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Lazy load images for upcoming slides
            this.preloadAdjacentSlides();
          }
        });
      }, {
        threshold: 0.1
      });
      
      observer.observe(this.element);
    }
  }
  
  preloadAdjacentSlides() {
    const preloadCount = 2;
    for (let i = 1; i <= preloadCount; i++) {
      const nextIndex = (this.currentIndex + i) % this.totalSlides;
      const prevIndex = (this.currentIndex - i + this.totalSlides) % this.totalSlides;
      
      this.preloadSlideImage(nextIndex);
      this.preloadSlideImage(prevIndex);
    }
  }
  
  preloadSlideImage(index) {
    const slide = this.slides[index];
    if (!slide) return;
    
    const image = slide.querySelector('img[data-src]');
    if (image && image.dataset.src) {
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    }
  }
  
  goToSlide(index, direction = null) {
    if (this.isAnimating || index === this.currentIndex || this.totalSlides <= 1) return;
    
    this.isAnimating = true;
    const prevIndex = this.currentIndex;
    this.currentIndex = this.normalizeIndex(index);
    
    // Reset progress bar
    this.resetProgressBar();
    
    // Update slides
    this.updateSlides(prevIndex, direction);
    this.updateDots();
    this.updateCounter();
    this.updateAccessibility();
    
    // Reset autoplay
    if (this.autoplayEnabled && !this.isPaused()) {
      this.restartAutoplay();
    }
    
    // Trigger custom event
    this.element.dispatchEvent(new CustomEvent('slideChange', {
      detail: {
        currentIndex: this.currentIndex,
        previousIndex: prevIndex,
        totalSlides: this.totalSlides
      }
    }));
    
    // Preload next slides
    setTimeout(() => this.preloadAdjacentSlides(), 300);
    
    // Reset animation flag
    setTimeout(() => {
      this.isAnimating = false;
    }, this.getTransitionDuration());
  }
  
  updateSlides(prevIndex, direction) {
    const prevSlide = this.slides[prevIndex];
    const currentSlide = this.slides[this.currentIndex];
    
    if (!prevSlide || !currentSlide) return;
    
    // Remove active class from all slides
    this.slides.forEach(slide => {
      slide.classList.remove('active');
      slide.setAttribute('aria-hidden', 'true');
    });
    
    // Add active class to current slide
    currentSlide.classList.add('active');
    currentSlide.setAttribute('aria-hidden', 'false');
    
    // Apply transition effect
    this.applyTransitionEffect(prevSlide, currentSlide, direction);
  }
  
  applyTransitionEffect(prevSlide, currentSlide, direction) {
    const duration = this.getTransitionDuration();
    
    switch(this.transitionEffect) {
      case 'fade':
        this.applyFadeTransition(prevSlide, currentSlide, duration);
        break;
      case 'zoom':
        this.applyZoomTransition(prevSlide, currentSlide, duration);
        break;
      case 'flip':
        this.applyFlipTransition(prevSlide, currentSlide, duration, direction);
        break;
      case 'slide':
      default:
        this.applySlideTransition(prevSlide, currentSlide, duration, direction);
        break;
    }
  }
  
  applyFadeTransition(prevSlide, currentSlide, duration) {
    currentSlide.style.transition = `opacity ${duration}ms ease`;
    prevSlide.style.transition = `opacity ${duration}ms ease`;
    
    requestAnimationFrame(() => {
      prevSlide.style.opacity = '0';
      currentSlide.style.opacity = '1';
    });
  }
  
  applySlideTransition(prevSlide, currentSlide, duration, direction) {
    const slideWidth = this.container.offsetWidth;
    const slideDirection = direction || (this.currentIndex > this.slides.indexOf(prevSlide) ? 'next' : 'prev');
    
    prevSlide.style.transition = `transform ${duration}ms ease`;
    currentSlide.style.transition = `transform ${duration}ms ease`;
    
    if (slideDirection === 'next') {
      currentSlide.style.transform = `translateX(${slideWidth}px)`;
      prevSlide.style.transform = `translateX(0)`;
      
      requestAnimationFrame(() => {
        currentSlide.style.transform = `translateX(0)`;
        prevSlide.style.transform = `translateX(-${slideWidth}px)`;
      });
    } else {
      currentSlide.style.transform = `translateX(-${slideWidth}px)`;
      prevSlide.style.transform = `translateX(0)`;
      
      requestAnimationFrame(() => {
        currentSlide.style.transform = `translateX(0)`;
        prevSlide.style.transform = `translateX(${slideWidth}px)`;
      });
    }
    
    // Clean up transforms after animation
    setTimeout(() => {
      prevSlide.style.transform = '';
      currentSlide.style.transform = '';
      prevSlide.style.transition = '';
      currentSlide.style.transition = '';
    }, duration);
  }
  
  applyZoomTransition(prevSlide, currentSlide, duration) {
    currentSlide.style.transition = `transform ${duration}ms ease, opacity ${duration}ms ease`;
    prevSlide.style.transition = `transform ${duration}ms ease`;
    
    requestAnimationFrame(() => {
      prevSlide.style.transform = 'scale(1.1)';
      prevSlide.style.opacity = '0';
      currentSlide.style.transform = 'scale(1)';
      currentSlide.style.opacity = '1';
    });
    
    setTimeout(() => {
      prevSlide.style.transform = '';
      prevSlide.style.opacity = '';
      currentSlide.style.transform = '';
      currentSlide.style.opacity = '';
      prevSlide.style.transition = '';
      currentSlide.style.transition = '';
    }, duration);
  }
  
  applyFlipTransition(prevSlide, currentSlide, duration, direction) {
    const slideDirection = direction || (this.currentIndex > this.slides.indexOf(prevSlide) ? 'next' : 'prev');
    const flipDirection = slideDirection === 'next' ? -1 : 1;
    
    prevSlide.style.transition = `transform ${duration}ms ease`;
    currentSlide.style.transition = `transform ${duration}ms ease`;
    prevSlide.style.transformOrigin = slideDirection === 'next' ? 'right center' : 'left center';
    currentSlide.style.transformOrigin = slideDirection === 'next' ? 'left center' : 'right center';
    
    requestAnimationFrame(() => {
      prevSlide.style.transform = `rotateY(${90 * flipDirection}deg)`;
      currentSlide.style.transform = `rotateY(${-90 * flipDirection}deg)`;
      
      requestAnimationFrame(() => {
        currentSlide.style.transform = 'rotateY(0deg)';
      });
    });
    
    setTimeout(() => {
      prevSlide.style.transform = '';
      currentSlide.style.transform = '';
      prevSlide.style.transformOrigin = '';
      currentSlide.style.transformOrigin = '';
      prevSlide.style.transition = '';
      currentSlide.style.transition = '';
    }, duration);
  }
  
  nextSlide() {
    let nextIndex = this.currentIndex + 1;
    
    if (nextIndex >= this.totalSlides) {
      if (this.loopEnabled) {
        nextIndex = 0;
      } else {
        return;
      }
    }
    
    this.goToSlide(nextIndex, 'next');
  }
  
  prevSlide() {
    let prevIndex = this.currentIndex - 1;
    
    if (prevIndex < 0) {
      if (this.loopEnabled) {
        prevIndex = this.totalSlides - 1;
      } else {
        return;
      }
    }
    
    this.goToSlide(prevIndex, 'prev');
  }
  
  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive);
    });
  }
  
  updateCounter() {
    if (this.counterCurrent) {
      this.counterCurrent.textContent = this.currentIndex + 1;
    }
  }
  
  updateAccessibility() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index !== this.currentIndex);
    });
    
    // Update live region for screen readers
    const liveRegion = this.element.querySelector('[aria-live]');
    if (liveRegion) {
      liveRegion.textContent = `Slide ${this.currentIndex + 1} of ${this.totalSlides}`;
    }
  }
  
  startAutoplay() {
    if (!this.autoplayEnabled || this.totalSlides <= 1) return;
    
    this.stopAutoplay();
    
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
    
    this.startProgressBar();
  }
  
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    
    this.stopProgressBar();
  }
  
  pauseAutoplay() {
    if (this.autoplayInterval) {
      this.remainingTime = this.calculateRemainingTime();
      this.stopAutoplay();
      this.updatePauseButton(true);
    }
  }
  
  resumeAutoplay() {
    if (this.autoplayEnabled && this.remainingTime > 0) {
      setTimeout(() => {
        this.nextSlide();
        this.startAutoplay();
      }, this.remainingTime);
      this.updatePauseButton(false);
    } else if (this.autoplayEnabled) {
      this.startAutoplay();
      this.updatePauseButton(false);
    }
  }
  
  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
  
  togglePause() {
    if (this.isPaused()) {
      this.resumeAutoplay();
    } else {
      this.pauseAutoplay();
    }
  }
  
  isPaused() {
    return this.autoplayInterval === null && this.autoplayEnabled;
  }
  
  updatePauseButton(paused) {
    if (!this.pauseBtn) return;
    
    const pauseIcon = this.pauseBtn.querySelector('.icon-pause');
    const playIcon = this.pauseBtn.querySelector('.icon-play');
    
    if (paused) {
      pauseIcon.style.display = 'none';
      playIcon.style.display = 'block';
      this.pauseBtn.setAttribute('aria-label', 'Play slideshow');
      this.pauseBtn.setAttribute('data-paused', 'true');
    } else {
      pauseIcon.style.display = 'block';
      playIcon.style.display = 'none';
      this.pauseBtn.setAttribute('aria-label', 'Pause slideshow');
      this.pauseBtn.setAttribute('data-paused', 'false');
    }
  }
  
  startProgressBar() {
    if (!this.progressBar) return;
    
    this.stopProgressBar();
    this.startTime = Date.now();
    
    this.progressBar.style.transition = `width ${this.autoplaySpeed}ms linear`;
    this.progressBar.style.width = '100%';
    
    this.progressInterval = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      const remaining = Math.max(0, this.autoplaySpeed - elapsed);
      this.progressBar.setAttribute('aria-valuenow', 100 - (remaining / this.autoplaySpeed * 100));
    }, 50);
  }
  
  stopProgressBar() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    
    if (this.progressBar) {
      this.progressBar.style.transition = 'none';
      this.progressBar.style.width = '0%';
    }
  }
  
  resetProgressBar() {
    this.stopProgressBar();
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
    }
  }
  
  calculateRemainingTime() {
    if (!this.startTime || !this.progressBar) return this.autoplaySpeed;
    
    const elapsed = Date.now() - this.startTime;
    const progress = parseFloat(this.progressBar.style.width) / 100;
    const remaining = this.autoplaySpeed * (1 - progress);
    
    return Math.max(0, remaining);
  }
  
  normalizeIndex(index) {
    if (this.loopEnabled) {
      return (index % this.totalSlides + this.totalSlides) % this.totalSlides;
    }
    return Math.max(0, Math.min(index, this.totalSlides - 1));
  }
  
  getTransitionDuration() {
    return parseInt(getComputedStyle(this.element).getPropertyValue('--slideshow-transition-speed')) || 500;
  }
  
  setInitialSlideHeight() {
    this.updateSlideHeights();
  }
  
  updateSlideHeights() {
    const height = this.container.offsetHeight;
    this.slides.forEach(slide => {
      slide.style.height = `${height}px`;
    });
  }
  
  handleResize() {
    this.updateSlideHeights();
    
    // Re-initialize touch events if orientation changed
    if (this.mobileSwipeEnabled && 'ontouchstart' in window) {
      this.setupTouchEvents();
    }
  }
  
  destroy() {
    this.stopAutoplay();
    this.stopProgressBar();
    
    // Remove event listeners
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.prevSlide);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.nextSlide);
    if (this.pauseBtn) this.pauseBtn.removeEventListener('click', this.togglePause);
    
    this.dots.forEach(dot => dot.removeEventListener('click', this.goToSlide));
    
    // Remove resize observer if exists
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

// Initialize all slideshows on the page
document.addEventListener('DOMContentLoaded', () => {
  const slideshowElements = document.querySelectorAll('[data-section-type="slideshow"]');
  
  slideshowElements.forEach(element => {
    // Check if already initialized
    if (!element.__slideshow) {
      element.__slideshow = new Slideshow(element);
    }
  });
  
  // Handle Shopify section reloads
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-section-type="slideshow"]');
      if (section && !section.__slideshow) {
        section.__slideshow = new Slideshow(section);
      }
    });
    
    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector('[data-section-type="slideshow"]');
      if (section && section.__slideshow) {
        section.__slideshow.destroy();
        delete section.__slideshow;
      }
    });
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Slideshow;
}